import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { apiClient } from '@/App';
import { toast } from 'sonner';
import { 
  LayoutDashboard, FileText, Plus, LogOut, User, Clock, 
  CheckCircle, AlertCircle, Loader2, RefreshCw, Eye, ChevronRight
} from 'lucide-react';

const statusConfig = {
  in_analiza: { label: 'În analiză', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  documente_lipsa: { label: 'Documente lipsă', color: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle },
  in_procesare: { label: 'În procesare', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: RefreshCw },
  finalizat: { label: 'Finalizat', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle }
};

const DashboardSidebar = ({ user, activeTab, setActiveTab, onLogout }) => {
  const menuItems = [
    { id: 'orders', label: 'Comenzile mele', icon: FileText },
    { id: 'profile', label: 'Profil', icon: User },
  ];

  return (
    <aside className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-64px)] hidden lg:block">
      <div className="p-6">
        {/* User Info */}
        <div className="flex items-center gap-3 mb-8">
          <Avatar className="h-12 w-12">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-blue-100 text-blue-600 font-semibold">
              {user?.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-semibold text-slate-900 truncate max-w-[150px]">{user?.name}</p>
            <p className="text-sm text-slate-500 truncate max-w-[150px]">{user?.email}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="space-y-2">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              data-testid={`sidebar-${item.id}-btn`}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === item.id
                  ? 'bg-blue-50 text-blue-600 font-medium'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </button>
          ))}
        </nav>

        {/* New Order Button */}
        <Link to="/comanda" className="block mt-6" data-testid="sidebar-new-order-btn">
          <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2">
            <Plus className="h-4 w-4" />
            Comandă nouă
          </Button>
        </Link>

        {/* Logout */}
        <button
          onClick={onLogout}
          data-testid="sidebar-logout-btn"
          className="w-full flex items-center gap-3 px-4 py-3 mt-4 rounded-lg text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="h-5 w-5" />
          Deconectare
        </button>
      </div>
    </aside>
  );
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await apiClient.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status !== 401) {
        toast.error('Eroare la încărcarea comenzilor');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
      toast.success('V-ați deconectat cu succes');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="dashboard-logo-link">
            <img 
              src="/logo-andeni.png" 
              alt="AnDeni Time Solutions" 
              className="h-14 w-auto drop-shadow-md"
            />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/comanda" className="hidden sm:block" data-testid="dashboard-new-order-btn">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                <Plus className="h-4 w-4" />
                Comandă nouă
              </Button>
            </Link>
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout-btn"
              className="flex items-center gap-2 text-slate-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span className="hidden sm:inline">Deconectare</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <DashboardSidebar 
          user={user} 
          activeTab={activeTab} 
          setActiveTab={setActiveTab}
          onLogout={handleLogout}
        />

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8" data-testid="dashboard-main">
          {activeTab === 'orders' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">Comenzile mele</h1>
                  <p className="text-slate-600 mt-1">Vizualizați și gestionați comenzile dumneavoastră</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchOrders}
                  data-testid="refresh-orders-btn"
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Actualizează
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : orders.length === 0 ? (
                <Card className="bg-white border-slate-200">
                  <CardContent className="py-20 text-center">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Nicio comandă</h3>
                    <p className="text-slate-600 mb-6">Nu aveți încă nicio comandă plasată.</p>
                    <Link to="/comanda" data-testid="empty-state-new-order-btn">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                        <Plus className="h-4 w-4" />
                        Plasează prima comandă
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {orders.map((order, index) => {
                    const status = statusConfig[order.status] || statusConfig.in_analiza;
                    const StatusIcon = status.icon;
                    
                    return (
                      <Card 
                        key={order.order_id}
                        className="bg-white border-slate-200 hover:shadow-md transition-shadow"
                        data-testid={`order-card-${index}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-slate-900">
                                  Comanda #{order.order_id.slice(-8).toUpperCase()}
                                </h3>
                                <Badge className={`${status.color} border`}>
                                  <StatusIcon className="h-3 w-3 mr-1" />
                                  {status.label}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600">
                                {order.vehicle_brand} • {formatDate(order.created_at)}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {order.service_details?.map((service, idx) => (
                                  <Badge key={idx} variant="secondary" className="bg-slate-100 text-slate-600">
                                    {service.name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm text-slate-500">Total</p>
                                <p className="text-xl font-bold text-slate-900">{order.total_price} EUR</p>
                              </div>
                              <Link to={`/cont/comanda/${order.order_id}`} data-testid={`view-order-${index}-btn`}>
                                <Button variant="outline" size="sm" className="gap-2">
                                  <Eye className="h-4 w-4" />
                                  Detalii
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && (
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 mb-8">Profilul meu</h1>
              <Card className="bg-white border-slate-200 max-w-2xl">
                <CardContent className="p-8">
                  <div className="flex items-center gap-6 mb-8">
                    <Avatar className="h-20 w-20">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-blue-100 text-blue-600 text-2xl font-semibold">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h2 className="text-xl font-semibold text-slate-900">{user?.name}</h2>
                      <p className="text-slate-600">{user?.email}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Email</p>
                      <p className="text-slate-900">{user?.email}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Cont creat</p>
                      <p className="text-slate-900">
                        {user?.created_at ? formatDate(user.created_at) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
